import { useMemo } from 'react';
import { useGameStore } from '../../core/store';
import {
  EXIT_CHECKPOINT_Z,
  TUNNEL_START_Z,
} from '../../data/tunnel';
import { skillBoards } from '../../data/skillBoards';
import './tunnel-radar.css';

const MAP_RANGE = 14; // meters visible radius on radar

export function TunnelRadar({ compact = false }: { compact?: boolean }) {
  const runnerZ = useGameStore((s) => s.runnerZ);
  const runnerX = useGameStore((s) => s.runnerX);
  const runnerYaw = useGameStore((s) => s.runnerYaw);
  const unlocked = useGameStore((s) => s.unlockedSkills);

  const unlockedSet = useMemo(() => new Set(unlocked), [unlocked]);

  const distToExit = Math.max(0, EXIT_CHECKPOINT_Z - runnerZ);
  const progress = Math.min(
    1,
    Math.max(0, (runnerZ - TUNNEL_START_Z) / (EXIT_CHECKPOINT_Z - TUNNEL_START_Z)),
  );

  // Match TunnelWalker: fwd=(-sin y, -cos y), right=(cos y, -sin y)
  const cos = Math.cos(runnerYaw);
  const sin = Math.sin(runnerYaw);

  const toRadar = (wx: number, wz: number) => {
    const dx = wx - runnerX;
    const dz = wz - runnerZ;
    const localRight = dx * cos - dz * sin;
    const localFwd = -dx * sin - dz * cos;
    // CSS +y is down — map forward to screen up
    const px = (localRight / MAP_RANGE) * 42;
    const py = (-localFwd / MAP_RANGE) * 42;
    const clamped = Math.hypot(px, py) > 42;
    if (!clamped) return { x: px, y: py, edge: false };
    const a = Math.atan2(py, px);
    return { x: Math.cos(a) * 40, y: Math.sin(a) * 40, edge: true };
  };

  const exit = toRadar(0, EXIT_CHECKPOINT_Z);
  // 0° = up on radar
  const bearingDeg = (Math.atan2(exit.x, -exit.y) * 180) / Math.PI;

  const boards = skillBoards.map((b) => {
    const p = toRadar(b.wall === 'left' ? -1.8 : 1.8, b.z);
    return {
      id: b.id,
      ...p,
      on: unlockedSet.has(b.id),
      hidden: Math.hypot(b.z - runnerZ, 1.8) > MAP_RANGE * 1.15,
    };
  });

  return (
    <div
      className={`tunnel-radar${compact ? ' tunnel-radar--compact' : ''}`}
      role="img"
      aria-label={`Navigation. Exit ${distToExit.toFixed(0)} meters ahead.`}
    >
      <div className="tunnel-radar__ring" aria-hidden>
        <svg className="tunnel-radar__ticks" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="47" className="tunnel-radar__rim" />
          <circle cx="50" cy="50" r="34" className="tunnel-radar__inner" />
          <circle cx="50" cy="50" r="20" className="tunnel-radar__core" />
          {Array.from({ length: 24 }, (_, i) => {
            const a = (i / 24) * Math.PI * 2;
            const major = i % 6 === 0;
            const r0 = major ? 43 : 45;
            const r1 = 47.5;
            return (
              <line
                key={i}
                x1={50 + Math.cos(a) * r0}
                y1={50 + Math.sin(a) * r0}
                x2={50 + Math.cos(a) * r1}
                y2={50 + Math.sin(a) * r1}
                className={
                  major ? 'tunnel-radar__tick is-major' : 'tunnel-radar__tick'
                }
              />
            );
          })}
          <path
            className="tunnel-radar__sweep"
            d="M50 50 L50 6 A44 44 0 0 1 78 14 Z"
          />
        </svg>

        <div className="tunnel-radar__world">
          {/* Exit route line */}
          <i
            className="tunnel-radar__route"
            style={{
              height: `${Math.min(40, Math.hypot(exit.x, exit.y))}px`,
              transform: `translate(-50%, -100%) rotate(${bearingDeg}deg)`,
            }}
          />

          {boards.map(
            (b) =>
              !b.hidden && (
                <i
                  key={b.id}
                  className={`tunnel-radar__board${b.on ? ' is-on' : ''}`}
                  style={{
                    transform: `translate(calc(-50% + ${b.x}px), calc(-50% + ${b.y}px))`,
                  }}
                />
              ),
          )}

          {/* Destination nav diamond */}
          <div
            className={`tunnel-radar__dest${exit.edge ? ' is-edge' : ''}`}
            style={{
              transform: `translate(calc(-50% + ${exit.x}px), calc(-50% + ${exit.y}px))`,
            }}
          >
            <span className="tunnel-radar__dest-pulse">
              <svg viewBox="0 0 24 24" aria-hidden>
                <path d="M12 2 L20 12 L12 22 L4 12 Z" />
                <path
                  d="M12 7 L16 12 L12 17 L8 12 Z"
                  className="tunnel-radar__dest-core"
                />
              </svg>
            </span>
          </div>

          {/* Player — fixed center, faces up */}
          <div className="tunnel-radar__player">
            <svg viewBox="0 0 24 24" aria-hidden>
              <path d="M12 3 L19 20 L12 16 L5 20 Z" />
            </svg>
          </div>
        </div>

        <div
          className="tunnel-radar__progress"
          style={{ ['--radar-p' as string]: String(progress) }}
        />
      </div>

      <div className="tunnel-radar__meta">
        <span className="tunnel-radar__label">NAV</span>
        <strong className="tunnel-radar__dist">
          {distToExit < 1 ? 'ARRIVE' : `${distToExit.toFixed(0)}m`}
        </strong>
      </div>
    </div>
  );
}
